/**
 * FormRecommendations Component
 *
 * Phase 7.2: Recommendation Engine
 *
 * Displays recommended forms based on job context:
 * - User's custom forms that match the job
 * - Suggestions for forms to consider uploading
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FileText,
  Sparkles,
  Star,
  Download,
  Upload,
  Lightbulb,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useFormRecommendations,
  type MatchingForm,
  type FormSuggestion,
  type FormRecommendationsParams,
} from '@/hooks/useFormRecommendations';
import { FormFillDialog } from './FormFillDialog';

interface FormRecommendationsProps {
  jurisdictionId?: number | null;
  permitType?: string | null;
  jobScope?: string | null;
  jobId?: string | null;
  onFormFilled?: (pdfBase64: string, formName: string) => void;
  onUploadClick?: () => void;
  compact?: boolean;
}

export function FormRecommendations({
  jurisdictionId,
  permitType,
  jobScope,
  jobId,
  onFormFilled,
  onUploadClick,
  compact = false,
}: FormRecommendationsProps) {
  const [fillFormId, setFillFormId] = useState<string | null>(null);
  const [showAllMatching, setShowAllMatching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const params: FormRecommendationsParams = {
    jurisdictionId,
    permitType,
    jobScope,
    jobId,
  };

  const { data, isLoading, error } = useFormRecommendations(params, !!jurisdictionId);

  const handleFillForm = (formId: string) => {
    setFillFormId(formId);
  };

  const handleFormFilled = (pdfBase64: string, formName: string) => {
    setFillFormId(null);
    onFormFilled?.(pdfBase64, formName);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.success) {
    return null; // Silently fail - recommendations are optional
  }

  const { recommendations, stats } = data;
  const { matchingForms, suggestions } = recommendations;

  // Nothing to show
  if (matchingForms.length === 0 && suggestions.length === 0) {
    if (compact) return null;

    return (
      <Card className="border-dashed">
        <CardContent className="py-6">
          <div className="text-center">
            <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No form recommendations yet
            </p>
            {onUploadClick && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={onUploadClick}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload a Form
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact view - just show count badge
  if (compact && matchingForms.length > 0) {
    const bestMatch = matchingForms[0];
    return (
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
          <Sparkles className="h-3 w-3 mr-1" />
          {matchingForms.length} form{matchingForms.length !== 1 ? 's' : ''} ready
        </Badge>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="h-7 bg-purple-600 hover:bg-purple-700"
                onClick={() => handleFillForm(bestMatch.id)}
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Fill
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs font-medium">{bestMatch.form_name}</p>
              <p className="text-xs text-muted-foreground">{bestMatch.matchReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Fill Dialog */}
        {fillFormId && (
          <FormFillDialog
            open={!!fillFormId}
            onClose={() => setFillFormId(null)}
            formId={fillFormId}
            jobId={jobId ?? undefined}
            onFilled={handleFormFilled}
          />
        )}
      </div>
    );
  }

  // Full view
  const displayedMatchingForms = showAllMatching
    ? matchingForms
    : matchingForms.slice(0, 3);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          Form Recommendations
        </CardTitle>
        <CardDescription>
          {matchingForms.length > 0
            ? `${matchingForms.length} form${matchingForms.length !== 1 ? 's' : ''} ready to fill`
            : 'Consider uploading these forms'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Matching Forms */}
        {matchingForms.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Your Forms
            </h4>
            <div className="space-y-2">
              {displayedMatchingForms.map((form) => (
                <MatchingFormCard
                  key={form.id}
                  form={form}
                  onFillClick={() => handleFillForm(form.id)}
                />
              ))}
            </div>
            {matchingForms.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => setShowAllMatching(!showAllMatching)}
              >
                {showAllMatching ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    Show {matchingForms.length - 3} More
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="space-y-2">
            <button
              className="w-full text-left"
              onClick={() => setShowSuggestions(!showSuggestions)}
            >
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                Suggested Forms
                <Badge variant="outline" className="text-xs ml-auto">
                  {suggestions.length}
                </Badge>
                {showSuggestions ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </h4>
            </button>

            {showSuggestions && (
              <div className="space-y-2 pt-2">
                {suggestions.slice(0, 5).map((suggestion) => (
                  <SuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onUploadClick={onUploadClick}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload prompt if no matching forms */}
        {matchingForms.length === 0 && onUploadClick && (
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              className="w-full"
              onClick={onUploadClick}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Your Forms
            </Button>
          </div>
        )}
      </CardContent>

      {/* Fill Dialog */}
      {fillFormId && (
        <FormFillDialog
          open={!!fillFormId}
          onClose={() => setFillFormId(null)}
          formId={fillFormId}
          jobId={jobId ?? undefined}
          onFilled={handleFormFilled}
        />
      )}
    </Card>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface MatchingFormCardProps {
  form: MatchingForm;
  onFillClick: () => void;
}

function MatchingFormCard({ form, onFillClick }: MatchingFormCardProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
          <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{form.form_name}</p>
            {form.is_favorite && (
              <Star className="h-3 w-3 fill-amber-400 text-amber-400 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {form.matchReason}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        className="bg-purple-600 hover:bg-purple-700 shrink-0 ml-2"
        onClick={onFillClick}
      >
        <Sparkles className="h-3.5 w-3.5 mr-1" />
        Fill
      </Button>
    </div>
  );
}

interface SuggestionCardProps {
  suggestion: FormSuggestion;
  onUploadClick?: () => void;
}

function SuggestionCard({ suggestion, onUploadClick }: SuggestionCardProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50">
          <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{suggestion.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {suggestion.purpose}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs">
              {suggestion.source === 'jurisdiction' ? 'Jurisdiction' : 'Template'}
            </Badge>
            {suggestion.fieldCount && (
              <span className="text-xs text-muted-foreground">
                {suggestion.fieldCount} fields
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2">
        {suggestion.pdfUrl && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  asChild
                >
                  <a
                    href={suggestion.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download template</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {suggestion.officialSource && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  asChild
                >
                  <a
                    href={suggestion.officialSource}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Official source</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {onUploadClick && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={onUploadClick}
          >
            <Upload className="h-3.5 w-3.5 mr-1" />
            Upload
          </Button>
        )}
      </div>
    </div>
  );
}

export default FormRecommendations;
